import React, { useState, memo, ReactElement, isValidElement } from 'react';
import Grid from '@jbhi-fi/lanyard-ui/components/Grid';
import { Slider } from '../slider/Slider';
import { ImageModal } from '../modal/ImageModal';
import Image from '../../../common/Image';
import UnmountAtBreakpoint from '../../../common/UnmountAtBreakpoint';
import { styles } from './Gallery.css';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { ProductImageFallback } from '@jbhi-fi/components/src/ProductImageFallback/ProductImageFallback';

export type GalleryProps = {
  view: string;
  images: Array<string | ReactElement>;
  title: string;
};

export const Gallery = memo(({ view, images, title }: GalleryProps): JSX.Element => {
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const hasImages = images?.length > 0;
  return (
    <>
      <UnmountAtBreakpoint breakpoint="md" matchRule="down">
        {!hasImages ? (
          <div className={styles.imageContainerSingle}>
            <ProductImageFallback />
          </div>
        ) : null}
        {view === 'gallery' && hasImages ? (
          <Grid container gutter="none" className={styles.gallery}>
            <Grid item lg={3} md={2} sm={4}>
              <div className={styles.thumbnailContainer}>
                {images.map((image, index) => (
                  <a
                    key={`image-${index}`}
                    href=""
                    onClick={e => {
                      e.preventDefault();
                      setCurrentImage(index);
                    }}
                  >
                    {isValidElement(image) ? (
                      image
                    ) : (
                      <Image
                        src={image as string}
                        width={65}
                        srcSetWidths={[65, 130, 195]}
                        sizes="65px"
                        alt={title}
                        className={currentImage === index ? styles.currentThumbnail : styles.thumbnail}
                        lazyload={false}
                      />
                    )}
                  </a>
                ))}
              </div>
            </Grid>
            <Grid item lg={9} md={8} sm={8} className={styles.currentImageContainer}>
              <a
                onClick={() => {
                  setShowModal(true);
                }}
              >
                {isValidElement(images[currentImage]) ? (
                  images[currentImage]
                ) : (
                  <Image
                    key={`image-${images[currentImage]}`}
                    src={images[currentImage] as string}
                    width={467}
                    srcSetWidths={[467, 934, 1401]}
                    sizes="(max-width: 950px) 100vw, 467px"
                    alt={title}
                    className={styles.mainImage}
                    lazyload={false}
                  />
                )}
              </a>
            </Grid>
          </Grid>
        ) : null}
        {view === 'grid' && hasImages ? (
          <div className={styles.gallery}>
            <div className={images.length > 1 ? styles.imageContainer : styles.imageContainerSingle}>
              {images.map((image, index) => (
                <a
                  key={`image-${index}`}
                  onClick={() => {
                    setCurrentImage(index);
                    setShowModal(true);
                  }}
                >
                  {isValidElement(image) ? (
                    image
                  ) : (
                    <Image
                      src={image as string}
                      width={301}
                      srcSetWidths={[301, 602, 903]}
                      alt={title}
                      lazyload={index > 3}
                    />
                  )}
                </a>
              ))}
            </div>
          </div>
        ) : null}
        <ImageModal
          sortedImages={images}
          title={title}
          toggleModal={setShowModal}
          showModal={showModal}
          initialSlide={currentImage}
        />
      </UnmountAtBreakpoint>
      <UnmountAtBreakpoint breakpoint="md" matchRule="up">
        <div className={styles.mobile}>
          {images.length === 0 ? (
            <div className={styles.gallery}>
              <div className={styles.imageContainerSingle}>
                <ProductImageFallback className={styles.imageSkeleton} />
              </div>
            </div>
          ) : images.length > 1 ? (
            <Slider sortedImages={images} title={title} imageWidth={65} imageSizes="100%" />
          ) : (
            <>
              <div className={styles.gallery}>
                <div className={styles.imageContainerSingle}>
                  <a
                    key={`image-${images[currentImage]}`}
                    onClick={() => {
                      setCurrentImage(currentImage);
                      setShowModal(true);
                    }}
                  >
                    {isValidElement(images[currentImage]) ? (
                      images[currentImage]
                    ) : (
                      <Image
                        src={images[currentImage] as string}
                        width={301}
                        srcSetWidths={[301, 602, 903]}
                        alt={title}
                        lazyload={false}
                      />
                    )}
                  </a>
                </div>
              </div>
              <ImageModal
                sortedImages={images}
                title={title}
                toggleModal={setShowModal}
                showModal={showModal}
                initialSlide={currentImage}
              />
            </>
          )}
        </div>
      </UnmountAtBreakpoint>
    </>
  );
});

Gallery.displayName = 'Gallery';
